<script setup>
import { computed, useId } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  preventCenter: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

// Stable per-instance id so the caption and toggle labels associate with the checkbox (S6853).
const switchId = useId();

const toggle = () => {
  if (props.disabled) return;
  emit('update:modelValue', !props.modelValue);
  emit('change', !props.modelValue);
};

const handleChange = (event) => {
  if (props.disabled) {
    event.target.checked = props.modelValue ?? false;
    return;
  }
  emit('update:modelValue', event.target.checked);
  emit('change', event.target.checked);
};

const rootClasses = computed(() => [
  'flex items-center gap-2 min-h-6',
  !props.label && !props.preventCenter ? 'justify-center' : '',
  props.disabled ? 'opacity-65 cursor-not-allowed' : '',
]);

const labelClasses = computed(() => [
  'flex-1 text-fg',
  props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
]);

const wrapperClasses = computed(() => [
  'flex items-center',
  props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
]);

// Slider-bakgrunn: fg-muted i av-tilstand, accent i på-tilstand.
const sliderClasses = computed(() => [
  'absolute inset-0 rounded-full transition-colors duration-200',
  // Thumb (::before-tilsvarende via after:): hvit/on-accent sirkel som glir til høyre når checked.
  'after:content-[""] after:absolute after:left-[3px] after:bottom-[3px] after:h-4 after:w-4 after:rounded-full after:bg-fg-on-accent after:transition-transform after:duration-200',
  props.modelValue ? 'bg-accent after:translate-x-[18px]' : 'bg-fg-muted',
  props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
]);
</script>

<template>
  <div
    :class="rootClasses"
    :title="title || undefined"
    @click.stop.prevent="toggle"
  >
    <label
      v-if="label"
      :for="switchId"
      :class="labelClasses"
      @click.stop.prevent
    >{{ label }}</label>
    <div :class="wrapperClasses">
      <label class="relative inline-block w-10 h-[22px]" :for="switchId" aria-label="på/av">
        <input
          :id="switchId"
          type="checkbox"
          class="opacity-0 w-0 h-0"
          :checked="modelValue ?? false"
          :disabled="disabled"
          @change.stop.prevent="handleChange"
        >
        <span :class="sliderClasses" />
      </label>
    </div>
  </div>
</template>
